import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, SegmentedControl, SparkIcon } from '@spark';

import { useAuth } from '@/features/auth/AuthContext';
import '../settings.css';

// Alias is derived from account type — not user-selectable.
// same_account → dev, cross_account → prod (max one target per alias).
function aliasForAccountType(accountType) {
    return accountType === 'cross_account' ? 'prod' : 'dev';
}

// Mirrors the backend regex in deployment_config_builder._build_ecr_target:
// <account>.dkr.ecr.<region>.amazonaws.com/<repository>
const ECR_REPOSITORY_URI_RE =
    /^\d{12}\.dkr\.ecr\.[a-z0-9-]+\.amazonaws\.com\/[a-z0-9][a-z0-9._/-]*$/;

function validateEcrRepositoryUri(uri) {
    const trimmed = (uri || '').trim();
    if (!trimmed) return 'ECR repository URI is required';
    if (!ECR_REPOSITORY_URI_RE.test(trimmed)) {
        return 'Use <account-id>.dkr.ecr.<region>.amazonaws.com/<repository>';
    }
    return null;
}

function normalizeTargets(targets) {
    return (targets || []).map((t) => {
        const account_type = t.account_type || 'same_account';
        return { uri: t.uri || '', account_type, alias: aliasForAccountType(account_type) };
    });
}

function targetsEqual(a, b) {
    if (a.length !== b.length) return false;
    return a.every((t, i) => {
        const o = b[i];
        return (
            (t.uri || '') === (o.uri || '') &&
            (t.account_type || '') === (o.account_type || '') &&
            (t.alias || '') === (o.alias || '')
        );
    });
}

const ACCOUNT_TYPE_ITEMS = [
    { id: 'same_account', label: 'Same account' },
    { id: 'cross_account', label: 'Cross account' },
];

/**
 * ECR push targets (deployment) settings — Spark rebuild.
 *
 * A DevOps user configures the platform's "Push to ECR" destinations here:
 *  - The cross-account assume-role (used only for ``cross_account`` targets).
 *  - A list of named ECR repository URIs, each tagged with a same/cross
 *    account type and an environment alias (``prod`` / ``dev``).
 */
const EcrCredentialsForm = ({ settings, onEcrSettingsUpdate }) => {
    const { role, isAuthEnabled } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';

    const [assumeRoleArn, setAssumeRoleArn] = useState(
        settings?.aws_ecr_assume_role_arn || ''
    );
    const [targets, setTargets] = useState(normalizeTargets(settings?.aws_ecr_targets));
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setAssumeRoleArn(settings?.aws_ecr_assume_role_arn || '');
    }, [settings?.aws_ecr_assume_role_arn]);

    useEffect(() => {
        setTargets(normalizeTargets(settings?.aws_ecr_targets));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(settings?.aws_ecr_targets || [])]);

    const savedTargets = useMemo(
        () => normalizeTargets(settings?.aws_ecr_targets),
        [settings?.aws_ecr_targets]
    );

    const roleDirty = (assumeRoleArn || '') !== (settings?.aws_ecr_assume_role_arn || '');
    const targetsDirty = !targetsEqual(targets, savedTargets);
    const isDirty = roleDirty || targetsDirty;

    const targetErrors = targets.map((t) => validateEcrRepositoryUri(t.uri));
    const hasTargetErrors = targetErrors.some(Boolean);
    // A cross-account target needs the assume-role configured to actually push.
    const crossAccountMissingRole =
        targets.some((t) => t.account_type === 'cross_account') &&
        !(assumeRoleArn || '').trim();

    const hasSameAccount = targets.some((t) => t.account_type === 'same_account');
    const hasCrossAccount = targets.some((t) => t.account_type === 'cross_account');

    const handleTargetChange = (index, key, value) => {
        setTargets((prev) =>
            prev.map((t, i) => {
                if (i !== index) return t;
                const updated = { ...t, [key]: value };
                if (key === 'account_type') updated.alias = aliasForAccountType(value);
                return updated;
            })
        );
    };

    const handleAddTarget = (accountType) => {
        setTargets((prev) => [
            ...prev,
            { uri: '', account_type: accountType, alias: aliasForAccountType(accountType) },
        ]);
    };

    const handleRemoveTarget = (index) => {
        setTargets((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        if (hasTargetErrors) return;
        const fields = {
            aws_ecr_assume_role_arn: (assumeRoleArn || '').trim(),
            aws_ecr_targets: targets.map((t) => {
                const account_type = t.account_type || 'same_account';
                return {
                    uri: (t.uri || '').trim(),
                    account_type,
                    alias: aliasForAccountType(account_type),
                };
            }),
        };
        setIsSaving(true);
        onEcrSettingsUpdate(fields, 'saved');
        setTimeout(() => setIsSaving(false), 500);
    };

    return (
        <div className="settings-providers__panel">
            <p className="settings-text settings-providers__description fs-unmask">
                Add ECR repositories. Each target is automatically aliased:
                same-account → dev, cross-account → prod. Same-account targets
                use the platform pod&apos;s IAM identity (IRSA); cross-account
                targets assume the role ARN configured below.
            </p>

            <h3 className="settings-subheading fs-unmask">Targets</h3>

            {targets.length === 0 ? (
                <p className="settings-text fs-unmask">
                    No ECR targets configured yet. Add one to enable
                    &quot;Push to ECR&quot;.
                </p>
            ) : null}

            {targets.map((target, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="settings-target-row">
                    <div className="settings-target-row__field">
                        <Input
                            size="large"
                            showLabel={false}
                            value={target.uri}
                            onChange={(e) => handleTargetChange(index, 'uri', e.target.value)}
                            placeholder="123456789012.dkr.ecr.us-west-2.amazonaws.com/specificai/specificai-inference"
                            disabled={!isAdmin}
                            className="settings-secret__input"
                        />
                        {targetErrors[index] ? (
                            <p className="settings-field-error">{targetErrors[index]}</p>
                        ) : null}
                    </div>
                    <SegmentedControl
                        size="small"
                        items={ACCOUNT_TYPE_ITEMS.map((item) => ({
                            ...item,
                            disabled: !isAdmin,
                        }))}
                        activeId={target.account_type}
                        onChange={(value) => handleTargetChange(index, 'account_type', value)}
                    />
                    <span className="settings-target-row__alias">{target.alias}</span>
                    <Button
                        size="tiny"
                        variant="ghost"
                        iconSlug="trash"
                        aria-label="Remove target"
                        startIcon={<SparkIcon slug="trash" size="small" />}
                        onClick={() => handleRemoveTarget(index)}
                        disabled={!isAdmin}
                    />
                </div>
            ))}

            <div className="settings-form__row">
                {!hasSameAccount ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        showStartIcon
                        onClick={() => handleAddTarget('same_account')}
                        disabled={!isAdmin}
                    >
                        Add Dev Target
                    </Button>
                ) : null}
                {!hasCrossAccount ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        showStartIcon
                        onClick={() => handleAddTarget('cross_account')}
                        disabled={!isAdmin}
                    >
                        Add Prod Target
                    </Button>
                ) : null}
            </div>

            <h3 className="settings-subheading fs-unmask">Cross-account role</h3>
            <p className="settings-text settings-providers__description fs-unmask">
                Only used for cross-account targets: the platform assumes this
                role in the target account before requesting an ECR token.
            </p>
            {crossAccountMissingRole ? (
                <p className="settings-field-error fs-unmask">
                    A cross-account target is configured but no role ARN is set.
                    Cross-account pushes will fail until you add it.
                </p>
            ) : null}
            <Input
                size="large"
                label="Cross-account role ARN"
                value={assumeRoleArn}
                onChange={(e) => setAssumeRoleArn(e.target.value)}
                placeholder="arn:aws:iam::123456789012:role/specificai-ecr-push"
                disabled={!isAdmin}
                className="settings-secret__input"
            />

            <div className="settings-form__footer">
                <Button
                    size="tiny"
                    variant="primary"
                    onClick={handleSave}
                    disabled={!isAdmin || !isDirty || hasTargetErrors || isSaving}
                    disableWhenReadOnly
                >
                    Save Changes
                </Button>
            </div>
        </div>
    );
};

export default EcrCredentialsForm;
